import { useMemo, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { useApp } from "@/store/AppStore";
import { primaryButtonClass, ghostButtonClass } from "@/components/base/fieldStyles";
import { formatNumber } from "@/utils/format";
import {
  computeRowStatus,
  parsePdfCatalog,
  type CatalogImportRow,
  type CatalogImportResult,
  type ExtractedImage,
  type PdfCatalogRow,
} from "@/utils/pdfCatalog";
import type { Category } from "@/types";
import PdfCatalogReviewTable from "@/pages/admin/respaldo/components/PdfCatalogReviewTable";
import CatalogImportPreviewModal from "@/pages/admin/respaldo/components/CatalogImportPreviewModal";

const CATEGORIES: Category[] = ["Aseo Personal", "Hogar", "Nutrición", "Belleza"];

// Módulo "Cargar / Actualizar Catálogo desde PDF Amway".
// Lee el PDF página por página (SKU, nombre, presentación, precio e imágenes),
// muestra una tabla de revisión editable y hace el merge NO destructivo por SKU.
const PdfCatalogPanel = () => {
  const { data, importCatalogProducts } = useApp();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [rows, setRows] = useState<PdfCatalogRow[]>([]);
  const [images, setImages] = useState<ExtractedImage[]>([]);
  const [pages, setPages] = useState(0);
  const [category, setCategory] = useState<Category>("Hogar");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState<string[]>([]);
  const [result, setResult] = useState<CatalogImportResult | null>(null);
  const [importing, setImporting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const reset = () => {
    setFileName("");
    setRows([]);
    setImages([]);
    setPages(0);
    setMessage("");
    setError("");
    setWarnings([]);
    setResult(null);
  };

  const handleFile = async (file: File) => {
    setError("");
    setMessage("");
    setResult(null);
    setWarnings([]);
    setRows([]);
    setImages([]);
    setBusy(true);
    try {
      const preview = await parsePdfCatalog(file, { fileName: file.name });
      const withStatus = preview.rows.map((r) => ({
        ...r,
        status: computeRowStatus(r, data.products),
      }));
      setFileName(file.name);
      setRows(withStatus);
      setImages(preview.images);
      setPages(preview.pages);
      setCategory(preview.suggestedCategory);
      setWarnings(preview.warnings);
      if (withStatus.length === 0) {
        setError(
          "No se detectaron productos con SKU/precio. Puede ser un PDF escaneado (imagen) o con otro formato."
        );
      } else {
        setMessage(
          `PDF analizado: ${formatNumber(withStatus.length)} productos detectados en ${formatNumber(
            preview.pages
          )} páginas. Revisa y edita antes de importar.`
        );
      }
    } catch {
      setError("No se pudo leer el PDF. Verifica que el archivo no esté dañado.");
    } finally {
      setBusy(false);
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    e.target.value = "";
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const updateRow = (id: string, patch: Partial<PdfCatalogRow>) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, ...patch };
        return { ...next, status: computeRowStatus(next, data.products) };
      })
    );
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const applyCategoryAll = (value: Category) => {
    setCategory(value);
    setRows((prev) =>
      prev.map((r) => ({ ...r, category: value, status: computeRowStatus({ ...r, category: value }, data.products) }))
    );
  };

  const runImport = async (options?: { applyNewPrices?: boolean; applyPriceSkus?: string[] }) => {
    if (rows.length === 0) return;
    setImporting(true);
    setError("");
    const payload: CatalogImportRow[] = rows.map((r) => ({
      sku: r.sku,
      name: r.name,
      category: r.category,
      pv: r.pv,
      pricePublic: r.pricePublic,
      imageUrl: r.imageUrl,
    }));
    const summary = await importCatalogProducts(payload, options);
    setImporting(false);
    if (summary) {
      setResult(summary);
      setRows([]);
      setImages([]);
      setPages(0);
      setFileName("");
      setMessage("");
      setWarnings([]);
    } else {
      setError("La importación no se pudo completar. Intenta de nuevo.");
    }
  };

  const photoCount = rows.filter((r) => r.imageUrl).length;

  // SKU que no existen todavía en el catálogo: los que se agregarían como NUEVOS.
  const nuevos = useMemo(
    () =>
      rows.filter((r) => {
        const sku = r.sku.trim().toLowerCase();
        return sku && !data.products.some((p) => p.sku.trim().toLowerCase() === sku);
      }),
    [rows, data.products]
  );

  // El botón principal abre SIEMPRE la confirmación: así ves exactamente qué
  // productos nuevos se agregarán antes de tocar nada (blindaje por factor humano).
  const confirmImport = async (options?: { applyNewPrices?: boolean; applyPriceSkus?: string[] }) => {
    setPreviewOpen(false);
    await runImport(options);
  };

  return (
    <div className="glass-panel rounded-lg p-4 md:p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-md bg-accent-500/20 text-accent-200 flex items-center justify-center">
          <i className="ri-file-pdf-2-line" />
        </span>
        <h3 className="editable-title text-base font-bold">Cargar / Actualizar Catálogo desde PDF Amway</h3>
      </div>
      <p className="text-[11px] text-foreground-500 mb-4">
        Lee el PDF del catálogo página por página y extrae <strong>SKU, nombre, presentación y
        precio público</strong>. Antes de guardar verás una tabla de revisión editable. Las
        <strong> fotos se cargan a mano</strong> por producto (subir o pegar). La sincronización es
        no destructiva: se compara por SKU, se crean los nuevos y se actualizan precio/PV/foto de
        los existentes, sin borrar ventas ni inventario.
      </p>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`rounded-lg border-2 border-dashed px-4 py-8 text-center cursor-pointer transition-colors ${
          dragging
            ? "border-accent-500/60 bg-accent-500/5"
            : "border-background-300/50 hover:border-accent-500/40"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf"
          onChange={onInputChange}
          className="hidden"
          aria-label="Seleccionar catálogo PDF"
        />
        <span className="mx-auto w-12 h-12 rounded-lg bg-background-200/60 text-accent-200 flex items-center justify-center mb-3">
          <i className="ri-file-upload-line text-2xl" />
        </span>
        <p className="text-sm font-semibold text-foreground-900">
          {fileName || "Arrastra el catálogo PDF de Amway aquí o haz clic para seleccionar"}
        </p>
        <p className="text-[11px] text-foreground-500 mt-1">Formato: .pdf (catálogo con texto seleccionable)</p>
      </div>

      {busy && (
        <p className="text-[11px] text-accent-200 mt-3 flex items-center gap-1.5">
          <i className="ri-loader-4-line animate-spin" /> Leyendo el PDF...
        </p>
      )}
      {message && !error && (
        <p className="text-[11px] text-accent-200 mt-3 flex items-center gap-1.5 animate-fade-up">
          <i className="ri-checkbox-circle-line" /> {message}
        </p>
      )}
      {error && (
        <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5 animate-fade-up">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}

      {warnings.length > 0 && (
        <ul className="mt-3 space-y-1">
          {warnings.map((w, i) => (
            <li key={i} className="text-[11px] text-primary-200 flex items-start gap-1.5">
              <i className="ri-alert-line mt-0.5" /> {w}
            </li>
          ))}
        </ul>
      )}

      {rows.length > 0 && (
        <div className="mt-5">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3 mb-3">
            <div className="grid grid-cols-3 gap-2 flex-1">
              <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
                <p className="text-[10px] text-foreground-500 flex items-center gap-1">
                  <i className="ri-store-2-line" /> Productos
                </p>
                <p className="text-sm font-bold text-foreground-950">{formatNumber(rows.length)}</p>
              </div>
              <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
                <p className="text-[10px] text-foreground-500 flex items-center gap-1">
                  <i className="ri-image-2-line" /> Con foto
                </p>
                <p className="text-sm font-bold text-foreground-950">{formatNumber(photoCount)}</p>
              </div>
              <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
                <p className="text-[10px] text-foreground-500 flex items-center gap-1">
                  <i className="ri-file-pdf-2-line" /> Páginas
                </p>
                <p className="text-sm font-bold text-foreground-950">{formatNumber(pages)}</p>
              </div>
            </div>
            <div className="lg:w-64">
              <label className="text-[10px] text-foreground-500 mb-1 block">
                Categoría del PDF (aplicar a todo)
              </label>
              <select
                value={category}
                onChange={(e) => applyCategoryAll(e.target.value as Category)}
                className="w-full rounded-md border border-background-300/50 bg-background-50 px-2 py-2 text-xs text-foreground-900 cursor-pointer"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <PdfCatalogReviewTable
            rows={rows}
            images={images}
            onUpdate={updateRow}
            onRemove={removeRow}
          />

          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <button onClick={() => setPreviewOpen(true)} disabled={importing} className={primaryButtonClass}>
              <i className="ri-list-check-2" /> Revisar y confirmar importación
            </button>
            <button onClick={reset} className={ghostButtonClass}>
              Descartar
            </button>
          </div>

          {/* Aviso visible en el momento de ofrecer la carga */}
          <div
            className={`mt-3 rounded-md border px-3 py-2.5 flex items-start gap-2 ${
              nuevos.length > 0
                ? "border-accent-500/40 bg-accent-500/10"
                : "border-background-300/40 bg-background-200/40"
            }`}
          >
            <i
              className={`${
                nuevos.length > 0 ? "ri-alert-line text-accent-300" : "ri-checkbox-circle-line text-foreground-500"
              } mt-0.5`}
            />
            <div>
              {nuevos.length > 0 ? (
                <>
                  <p className="text-xs font-semibold text-foreground-900">
                    Se agregarán {formatNumber(nuevos.length)} producto
                    {nuevos.length === 1 ? "" : "s"} NUEVO{nuevos.length === 1 ? "" : "S"}
                  </p>
                  <p className="text-[11px] text-foreground-600 mt-0.5">
                    Son SKU que hoy no están en tu catálogo. Se listan exactos en
                    "Revisar y confirmar importación".
                  </p>
                </>
              ) : (
                <p className="text-[11px] text-foreground-600">
                  Ningún producto nuevo: todos los SKU del PDF ya existen en tu catálogo.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {result && (
        <div className="mt-4 rounded-md border border-accent-500/30 bg-accent-500/5 px-4 py-3 animate-fade-up">
          <p className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-accent-300" /> Catálogo sincronizado
          </p>
          <p className="text-[11px] text-foreground-600 mt-1">
            {formatNumber(result.created)} productos nuevos · {formatNumber(result.updated)}{" "}
            actualizados · {formatNumber(result.images)} fotos cargadas · {formatNumber(result.skipped)}{" "}
            sin cambios (omitidos).
          </p>
          {result.warnings.length > 0 && (
            <ul className="mt-2 space-y-1">
              {result.warnings.map((w, i) => (
                <li key={i} className="text-[11px] text-primary-200 flex items-start gap-1.5">
                  <i className="ri-alert-line mt-0.5" /> {w}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <CatalogImportPreviewModal
        open={previewOpen}
        rows={rows}
        products={data.products}
        onClose={() => setPreviewOpen(false)}
        onConfirm={confirmImport}
        confirming={importing}
      />

      {rows.length === 0 && !result && !busy && (
        <p className="text-[10px] text-foreground-500 mt-3 flex items-start gap-1.5">
          <i className="ri-lightbulb-line mt-0.5" /> Sugerencia: nombra el PDF con la categoría
          (ej. <em>catalogo-hogar.pdf</em>) y la detección de categoría será automática.
        </p>
      )}
    </div>
  );
};

export default PdfCatalogPanel;